// What the checkout sends, lifted from PlaceOrderScreen (Oct 2026), which keeps the requests
// around them: the PayPal purchase order its createOrder creates, the cart item once its design
// is saved by POST /api/designs/place-order, and the order body POST /api/orders stores, with the
// PayPal transaction id read from the captured order.
import { calculateLineTotals } from './orderTotals';

// the PayPal order over the cart's total, with the shipping address PayPal shows and must not change; countryCode is the ISO code of the shipping zone's country the address matched
export const paypalOrderBody = (cart, countryCode) => ({
  purchase_units: [
    {
      amount: { value: cart.totalGross }, // the price in the paypal payment is set to the totalGross state
      shipping: {
        name: {
          full_name: cart.shippingAddress.fullName,
        },
        address: {
          address_line_1: cart.shippingAddress.companyName // the street line is built twice, as the screen had it: the lift is verbatim (Oct 2026)
            ? cart.shippingAddress.companyName
            : `${
                cart.shippingAddress.street +
                ' ' +
                cart.shippingAddress.houseNumber
              }`,
          address_line_2: cart.shippingAddress.companyName
            ? `${
                cart.shippingAddress.street +
                ' ' +
                cart.shippingAddress.houseNumber
              }`
            : '',
          admin_area_2: cart.shippingAddress.city,
          postal_code: cart.shippingAddress.postalCode,
          country_code: countryCode,
        },
      },
    },
  ],
  application_context: {
    // deprecated in the Orders API for payment_source.paypal.experience_context, like payer below: changes to watch
    shipping_preference: 'NO_SHIPPING', // the preference the screen has always sent, under a comment that said it fixed the address in PayPal; PayPal documents it as redacting the address from its window, which shows none (KNOWN_BUGS.md, the PayPal shipping preference)
  },
});

// the capture inside the PayPal order the buttons' capture() resolved. The fallback to the order's own id went (Oct 2026, user decision): no answer without a capture reaches the handler, as the SDK restarts the checkout itself on a declined instrument (PayPal's funding-failures guide; a declined card at the local app stayed inside PayPal's window with its own message, Oct 2026) and the REST minimal answer is a form the buttons don't request. One would now throw here, at step 6 of the approve handler, after the reservation, the invoice number and the uploads, instead of storing an order id as the transaction id; and an answer without the order id too failed the order at the route all the same, whose model requires the id. The payer read below trusts the full answer the same way
const capturedTransactionId = (details) =>
  details.purchase_units[0].payments.captures[0].id;

// the cart item of a customizer design once POST /api/designs/place-order saved it: the saved design's id and thumbnail replace the IndexedDB reference
export const placedCartItem = (item, savedDesign) => {
  const enrichedItem = {
    ...item,
    designId: savedDesign._id, // Add saved design ID
    thumbnail: savedDesign.metadata.thumbnail, // Add saved thumbnail URL
  };

  // Remove unnecessary fields
  delete enrichedItem.uniqueCartId;

  return enrichedItem;
};

// the order POST /api/orders stores: the items with their line totals, the cart's totals and addresses, the PayPal capture, and the browser's locale and time zone for the order's dates
export const orderBody = ({
  cart,
  orderItems,
  invoiceNumber,
  invoiceDate,
  details,
  language,
}) => {
  const enrichedOrderItemsWithLines = orderItems.map((it) => ({
    ...it,
    ...calculateLineTotals(it),
  }));

  return {
    orderId: invoiceNumber,
    paypalTransactionId: capturedTransactionId(details), // link to the PayPal transaction
    orderItems: enrichedOrderItemsWithLines,
    shippingAddress: cart.shippingAddress,
    billingAddress: cart.billingAddress,
    paymentMethod: cart.paymentMethod,
    itemsGross: cart.itemsGross,
    itemsNet: cart.itemsNet,
    itemsVat: cart.itemsVat,
    shippingGross: cart.shippingGross,
    shippingNet: cart.shippingNet,
    shippingVat: cart.shippingVat,
    shippingByVatRate: cart.shippingByVatRate,
    totalGross: cart.totalGross,
    totalNet: cart.totalNet,
    totalVat: cart.totalVat,
    isPaid: true, // for every capture PayPal resolves, a PENDING one too (KNOWN_BUGS.md, a pending capture)
    paidAt: Date.now(), // the browser's clock when the body is built, stored as sent (KNOWN_BUGS.md, the order route taking the body unchecked); the capture's own create_time sits in details
    paymentResult: {
      id: details.id,
      status: details.status,
      update_time: details.update_time,
      email_address: details.payer.email_address, // read as the screen always has; the Orders API marks payer deprecated for payment_source.paypal, a change to watch, not a form seen today
    },
    locale: navigator.language, // the browser's locale and time zone, for the order's dates; a browser always sets both, so their fallbacks ('en-US', 'UTC') went (Oct 2026, user decision), the route keeping its own for crafted requests
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language, // based on the language the user selected, the language of the order email will vary
    invoiceDate,
  };
};
